import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

import type { Locale } from "@/i18n/resolve"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDateTime(value: string | null | undefined, locale: Locale): string {
  if (!value) return "—"
  return new Date(value).toLocaleString(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  })
}

/** A timestamp as wall-clock time in a given IANA zone, e.g. a schedule's own zone. */
export function formatInZone(
  value: string | null | undefined,
  timeZone: string,
  locale: Locale,
): string {
  if (!value) return "—"
  try {
    return new Date(value).toLocaleString(locale, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone,
    })
  } catch {
    return formatDateTime(value, locale)
  }
}

export function formatRelative(value: string | null | undefined, locale: Locale): string {
  if (!value) return "—"
  const delta = Date.now() - new Date(value).getTime()
  const minutes = Math.round(delta / 60000)
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto", style: "short" })
  if (Math.abs(minutes) < 1) return rtf.format(0, "minute")
  if (Math.abs(minutes) < 60) return rtf.format(-minutes, "minute")
  const hours = Math.round(minutes / 60)
  if (Math.abs(hours) < 24) return rtf.format(-hours, "hour")
  return rtf.format(-Math.round(hours / 24), "day")
}

export function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return "—"
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`
}

/** Estimated run cost in USD; "—" when the model has no configured price. */
export function formatCostUsd(cost: number | null | undefined): string {
  if (cost == null) return "—"
  return `$${cost.toFixed(4)}`
}
